import React, { useState, useEffect } from "react";
import { X, ExternalLink, Users, CheckCircle2, Clock, Lock } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { money } from "@/lib/format";
import { BudgetBar, PlatformPill, StatusChip, PosterBox } from "@/components/brand";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SubmitClip from "@/components/SubmitClip";

export default function CampaignDetail({ slug, open, onClose }) {
  const [c, setC] = useState(null);
  const [codeMode, setCodeMode] = useState(false);
  const [code, setCode] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const load = async () => {
    if (!slug) return;
    try { const { data } = await api.get(`/campaigns/${slug}`); setC(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { if (open) load(); }, [open, slug]);

  if (!open) return null;

  const join = async () => {
    if (!user) { nav("/login"); return; }
    try {
      if (c.join_mode === "approval") { await api.post(`/campaigns/${c.id}/request-join`); toast.success("Requested to join"); }
      else { await api.post(`/campaigns/${c.id}/join`); toast.success("Joined campaign"); }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const joinCode = async () => {
    try { await api.post("/campaigns/join-with-code", { code }); toast.success("Joined"); setCodeMode(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const renderJoinBtn = () => {
    if (!c) return null;
    if (c.private) return <button className="btn-brand pill focus-ring" onClick={() => setCodeMode(true)} style={btn}>Enter invite code</button>;
    if (c.budget_exhausted) return <button className="btn-brand pill" disabled style={btn}>Budget exhausted</button>;
    if (c.status === "ended") return <button className="btn-brand pill" disabled style={btn}>Ended</button>;
    if (!user) return <button className="btn-brand pill focus-ring" onClick={() => nav("/login")} style={btn}>Log in to join</button>;
    if (c.member_status === "active") return <button className="btn-outline pill focus-ring" disabled style={btn}>Joined</button>;
    if (c.member_status === "pending") return <button className="btn-outline pill" disabled style={btn}>Requested</button>;
    if (c.visibility === "private") return <button className="btn-brand pill focus-ring" onClick={() => setCodeMode(true)} style={btn}>Enter invite code</button>;
    if (c.join_mode === "approval") return <button className="btn-brand pill focus-ring" onClick={join} style={btn}>Request to join</button>;
    return <button className="btn-brand pill focus-ring" onClick={join} data-testid="detail-join" style={btn}>Join campaign</button>;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="p-0 gap-0" style={{ maxWidth: 620, background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--text)", borderRadius: 20, overflow: "hidden", padding: 0 }} data-testid="campaign-detail">
        {!c ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div>
        ) : c.private ? (
          <div>
            <PosterBox rounded={0} style={{ height: 150 }}>
              <button onClick={onClose} style={closeBtn}><X size={16} /></button>
            </PosterBox>
            <div style={{ padding: 28, textAlign: "center" }}>
              <Lock size={28} style={{ margin: "0 auto 12px", color: "var(--accent-light)" }} />
              <h3 style={{ margin: 0 }}>This campaign is private</h3>
              <p style={{ color: "var(--sub)", marginTop: 8 }}>Enter your invite code to view and join.</p>
              <div style={{ display: "flex", gap: 8, maxWidth: 320, margin: "16px auto 0" }}>
                <input className="input-dark" placeholder="Invite code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="detail-code-input" />
                <button className="btn-brand focus-ring" onClick={joinCode} style={{ padding: "0 16px", whiteSpace: "nowrap" }}>Join</button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ maxHeight: "88vh", overflowY: "auto" }}>
            <PosterBox poster={c.posters?.hero} alt={c.title} rounded={0} style={{ height: 190 }}>
              <div className="scrim-left" style={{ position: "absolute", inset: 0 }} />
              <button onClick={onClose} style={closeBtn} data-testid="detail-close"><X size={16} /></button>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", padding: 20 }}>
                <div style={{ fontSize: 13, color: "var(--banner-sub)" }}>{c.brand_name} × Clipup</div>
              </div>
              <div style={{ position: "absolute", bottom: 14, left: 18, right: 18 }}>
                <h2 style={{ margin: 0, color: "#fff", fontSize: 20 }}>{c.title}</h2>
                <p style={{ margin: "4px 0 0", color: "var(--banner-sub)", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.description?.slice(0, 90)}…</p>
              </div>
            </PosterBox>

            <div style={{ padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, justifyContent: "space-between" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  <StatusChip status={c.ending_soon ? "needs_review" : "active"} />
                  {c.platforms?.filter((p) => p.enabled).map((p) => <PlatformPill key={p.platform} platform={p.platform} label={p.label} />)}
                  <span className="chip">{c.category}</span>
                  <span className="chip"><Users size={12} /> {c.participants}</span>
                  <span className="chip">CPM {money(c.top_rate)}</span>
                </div>
                {renderJoinBtn()}
              </div>

              <Section title="About this campaign"><p style={{ color: "var(--sub)", lineHeight: 1.6 }}>{c.description}</p></Section>

              <Section title="Payout per platform">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 }}>
                  {c.platforms?.filter((p) => p.enabled).map((p) => (
                    <div key={p.platform} className="raised" style={{ borderRadius: 12, padding: 12 }}>
                      <div style={{ fontWeight: 500, marginBottom: 8 }}><PlatformPill platform={p.platform} label={p.label} /></div>
                      <Row k="Per 1K views" v={money(p.rate_per_1k)} />
                      <Row k="Max payout" v={money(p.max_payout)} />
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Budget">
                <div className="raised" style={{ borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 22, fontWeight: 500 }}>{money(c.paid_out)}</div>
                  <div style={{ fontSize: 12, color: "var(--sub)", marginBottom: 10 }}>paid out · {money(c.budget_remaining)} remaining</div>
                  <BudgetBar used={c.budget_used} total={c.budget_total} showLabel={false} />
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Paid out of {money(c.budget_total)} total budget</div>
                </div>
              </Section>

              {c.references?.length > 0 && (
                <Section title="Reference materials">
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {c.references.map((r, i) => (
                      <a key={i} href={r.url} target="_blank" rel="noreferrer" className="raised focus-ring" style={{ borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--accent-light)" }}>
                        {r.label} <ExternalLink size={14} />
                      </a>
                    ))}
                  </div>
                </Section>
              )}

              {c.rules?.length > 0 && (
                <Section title="Campaign rules">
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {c.rules.map((r, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, color: "var(--sub)", fontSize: 13 }}>
                        <CheckCircle2 size={16} style={{ color: "var(--accent-light)", flexShrink: 0 }} /> {r}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {c.member_status === "active" && (
                <Section title="Your submissions">
                  <button className="btn-brand focus-ring" onClick={() => setSubmitOpen(true)} data-testid="detail-submit-clip" style={{ padding: "10px 16px", marginBottom: 12 }}>Submit clip</button>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {(c.my_submissions || []).map((s) => (
                      <div key={s.id} className="raised" style={{ borderRadius: 10, padding: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <a href={s.url} target="_blank" rel="noreferrer" style={{ color: "var(--accent-light)", fontSize: 13 }}>{s.title || s.url?.slice(0, 40)}</a>
                        <StatusChip status={s.status} />
                      </div>
                    ))}
                    {(!c.my_submissions || c.my_submissions.length === 0) && <div style={{ color: "var(--muted)", fontSize: 13 }}>No submissions yet.</div>}
                  </div>
                </Section>
              )}
            </div>
          </div>
        )}
        <SubmitClip open={submitOpen} onClose={() => { setSubmitOpen(false); load(); }} campaignId={c?.id} />
      </DialogContent>
    </Dialog>
  );
}

const btn = { padding: "9px 20px", fontSize: 14 };
const closeBtn = { position: "absolute", top: 12, right: 12, zIndex: 3, width: 32, height: 32, borderRadius: 8, background: "rgba(0,0,0,.4)", border: "1px solid var(--border-strong)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };

function Section({ title, children }) {
  return <div style={{ marginTop: 20 }}><h4 style={{ fontSize: 14, color: "var(--sub)", margin: "0 0 10px" }}>{title}</h4>{children}</div>;
}
function Row({ k, v }) {
  return <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}><span style={{ color: "var(--muted)" }}>{k}</span><span>{v}</span></div>;
}
